import type { AdminMiscCredentialsPdf, CredentialCard } from '@/types';
import {
  type CardDesign,
  CredentialCardView,
} from './components/CredentialCardView';
import { DownloadAllButton } from './components/DownloadAllButton';
import { DownloadCardsPdfButton } from './components/DownloadCardsPdfButton';
import { DownloadSheetPdfButton } from './components/DownloadSheetPdfButton';

const MM_PER_INCH = 25.4;
const DPI = 150;
const PX_PER_MM = DPI / MM_PER_INCH;

const A3_W_MM = 297;
const A3_H_MM = 420;

const CARD_W_MM = 56;
const CARD_H_MM = 87;

const COL_X_MM = [12.0, 84.1, 156.1, 228.2];
const ROW_Y_MM = [116.5, 216.6];

const CARD_SOURCE_W_PX = 535;
const CARD_SOURCE_H_PX = 847;

const CARDS_PER_PAGE = 8;

const pageWidthPx = A3_W_MM * PX_PER_MM;
const pageHeightPx = A3_H_MM * PX_PER_MM;
const cardWidthPx = CARD_W_MM * PX_PER_MM;
const cardHeightPx = CARD_H_MM * PX_PER_MM;
const scaleX = cardWidthPx / CARD_SOURCE_W_PX;
const scaleY = cardHeightPx / CARD_SOURCE_H_PX;

interface SheetProps {
  name: string;
  exportName: string;
  cards: CredentialCard[];
  design: CardDesign;
}

function Sheet({ name, exportName, cards, design }: SheetProps) {
  const slots: (CredentialCard | null)[] = [...cards];
  while (slots.length < CARDS_PER_PAGE) slots.push(null);

  return (
    <div className="flex flex-col gap-2">
      <div className="font-mono text-gray-700 text-sm">{name}</div>
      <div
        data-pdf-page
        data-pdf-name={exportName}
        className="relative"
        style={{
          width: `${pageWidthPx}px`,
          height: `${pageHeightPx}px`,
          backgroundColor: '#ffffff',
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        }}
      >
        {slots.map((card, index) => {
          const left = COL_X_MM[index % 4] * PX_PER_MM;
          const top = ROW_Y_MM[Math.floor(index / 4)] * PX_PER_MM;

          return (
            <div
              key={card ? card.key : `empty-${name}-${index}`}
              style={{
                position: 'absolute',
                left: `${left}px`,
                top: `${top}px`,
                width: `${cardWidthPx}px`,
                height: `${cardHeightPx}px`,
                overflow: card ? 'hidden' : undefined,
              }}
            >
              {card && (
                <div
                  style={{
                    width: `${CARD_SOURCE_W_PX}px`,
                    height: `${CARD_SOURCE_H_PX}px`,
                    transform: `scale(${scaleX}, ${scaleY})`,
                    transformOrigin: 'top left',
                  }}
                >
                  <CredentialCardView card={card} design={design} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Pdf({
  title,
  subtitle,
  layout,
  filenameSlug,
  vectorPdfNotice,
  sheets,
  cards,
  card,
  dateLabel,
  cityLabel,
  sponsorsAsset,
  cardFootMarginTop,
}: AdminMiscCredentialsPdf) {
  const design = { card, dateLabel, cityLabel, sponsorsAsset, cardFootMarginTop };

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-bold text-3xl text-gray-900">{title}</h1>
          <p className="text-gray-600 text-sm">{subtitle}</p>
          {vectorPdfNotice && (
            <p className="mt-2 max-w-2xl rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900 text-sm">
              The buttons below export a flattened bitmap — the print provider
              rejects those. For the files you send them, run{' '}
              <code className="font-mono">
                npx dotenv-cli -e .env.local -- npx tsx
                src/lib/dev/scripts/credentials-vector-pdf.ts
              </code>{' '}
              and take the PDFs from{' '}
              <code className="font-mono">tmp/credentials-26-mx/</code>. This
              page stays as the on-screen preview.
            </p>
          )}
        </div>
        {layout === 'sheet' ? (
          <div className="flex items-center gap-2">
            <DownloadAllButton fallbackPrefix={`${filenameSlug}-sheet-`} />
            <DownloadSheetPdfButton
              a3WidthMm={A3_W_MM}
              a3HeightMm={A3_H_MM}
              renderDpi={DPI}
              filenameSlug={filenameSlug}
            />
          </div>
        ) : (
          <DownloadCardsPdfButton
            cardWidthMm={CARD_W_MM}
            cardHeightMm={CARD_H_MM}
            filenameSlug={filenameSlug}
          />
        )}
      </div>

      <div className="overflow-auto bg-gray-200 p-6">
        {layout === 'sheet' ? (
          <div className="flex flex-col items-center gap-12">
            {sheets.map((sheet) => (
              <Sheet
                key={sheet.name}
                name={sheet.name}
                exportName={sheet.exportName}
                cards={sheet.cards}
                design={design}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-wrap items-start justify-center gap-6">
            {cards.map((credential) => (
              <div
                key={credential.key}
                data-pdf-page
                style={{
                  width: `${CARD_SOURCE_W_PX}px`,
                  height: `${CARD_SOURCE_H_PX}px`,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                }}
              >
                <CredentialCardView
                  card={credential}
                  design={design}
                  rounded={false}
                />
              </div>
            ))}
          </div>
        )}

        {layout === 'card' && cards.length === 0 && (
          <div className="py-20 text-center">
            <p className="text-gray-600 text-lg">No credentials found</p>
          </div>
        )}
      </div>
    </div>
  );
}
